import { post } from "@/api";

/** 报表接口统一筛选参数 */
export interface ReportFilterParams {
  date_begin?: string;
  date_end?: string;
  department_ids?: Array<number | string>;
  goods_ids?: Array<number | string>;
  spec_ids?: Array<number | string>;
  /**
   * 查询模式：
   * - complete_time：按完成时间（默认）
   * - created_at：按下单时间
   */
  stat_mode?: "created_at" | "complete_time";
}

/** 汇总行：月汇总 d=YYYY-MM，日汇总 d=YYYY-MM-DD */
export interface ReportSummaryRow {
  d: string;
  department_id: number | string;
  department_name?: string | null;
  order_count?: number | string;
  goods_quantity?: number | string;
  amount?: number | string;
  cost?: number | string;
  total?: number | string;
}

export interface ReportSummaryResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ReportSummaryRow[];
  errors?: Record<string, string[]>;
}

/** 月度汇总表 */
export function getMonthSummary(params: ReportFilterParams) {
  return post<ReportSummaryResponse>("/platform/report/month-summary", params);
}

/** 日汇总表 */
export function getDailySummary(params: ReportFilterParams) {
  return post<ReportSummaryResponse>("/platform/report/daily-summary", params);
}

/** 物资汇总行：按规格型号汇总 */
export interface ReportGoodsSummaryRow {
  goods_id?: number | string;
  spec_id?: number | string;
  /** 物资类别（一级/二级） */
  category_name?: string | null;
  parent_category_id?: number | string | null;
  parent_category_name?: string | null;
  category_id?: number | string | null;
  child_category_name?: string | null;
  goods_name?: string | null;
  sku_name?: string | null;
  code?: string | null;
  quantity?: number | string;
  unit?: string | null;
  price?: number | string;
  amount?: number | string;
}

export interface ReportGoodsSummaryResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ReportGoodsSummaryRow[];
  errors?: Record<string, string[]>;
}

/** 物资汇总表 */
export function getGoodsSummary(params: ReportFilterParams) {
  return post<ReportGoodsSummaryResponse>("/platform/report/goods-summary", params);
}

/** 分类汇总行：按月 + 科室 + 一/二级分类 */
export interface ReportCategorySummaryRow {
  d: string;
  department_id: number | string;
  department_name?: string | null;
  /** 一级分类 */
  parent_category_id?: number | string | null;
  parent_category_name?: string | null;
  /** 二级分类（挂在一级下；若规格直接挂一级则为 null） */
  category_id?: number | string | null;
  category_name?: string | null;
  order_count?: number | string;
  /** 采购金额（明细金额，退货为负） */
  amount?: number | string;
  /** 分摊运费（按明细金额占订单物资金额比例） */
  cost?: number | string;
  /** 采购总额 = amount + cost */
  total?: number | string;
}

export interface ReportCategorySummaryResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ReportCategorySummaryRow[];
  errors?: Record<string, string[]>;
}

/** 分类汇总表（按月、科室、一/二级分类） */
export function getCategorySummary(params: ReportFilterParams) {
  return post<ReportCategorySummaryResponse>("/platform/report/category-summary", params);
}

export interface ReportDetailListItem {
  id: number | string;
  goods_id?: number | string;
  spec_id?: number | string;
  goods_name?: string | null;
  sku_name?: string | null;
  sku?: string | null;
  unit?: string | null;
  price?: number | string;
  quantity?: number | string;
  amount?: number | string;
}

/** 明细表：订单嵌套物资列表 */
export interface ReportDetailOrder {
  id: number | string;
  order_sn?: string;
  department_id?: number | string;
  department_name?: string | null;
  account_name?: string | null;
  created_at?: string | null;
  complete_time?: string | null;
  state?: string | null;
  is_backorder?: number | string;
  /** 物资合计 */
  number?: number | string;
  /** 运费 */
  cost?: number | string;
  /** 物资金额 */
  amount?: number | string;
  /** 订单金额 */
  total?: number | string;
  lists?: ReportDetailListItem[];
}

export interface ReportDetailsResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ReportDetailOrder[];
  errors?: Record<string, string[]>;
}

/** 订单明细表 */
export function getReportDetails(params: ReportFilterParams) {
  return post<ReportDetailsResponse>("/platform/report/details", params);
}

/** 年度预算：单月使用 */
export interface ReportPurchasePlanMonthRow {
  d: string;
  order_count?: number | string;
  used_amount?: number | string;
}

/** 年度预算：科室汇总（含各月） */
export interface ReportPurchasePlanRow {
  year: number | string;
  department_id: number | string;
  department_name?: string | null;
  /** 年度总预算 */
  quota?: number | string;
  /** 今年已用 */
  used_amount?: number | string;
  /** 使用比例（百分比，年维度） */
  usage_ratio?: number | string;
  /** 剩余可用 */
  remain_amount?: number | string;
  months?: ReportPurchasePlanMonthRow[];
}

export interface ReportPurchasePlanResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ReportPurchasePlanRow[];
  errors?: Record<string, string[]>;
}

/** 年度预算统计表（is_limit_quota + purchase_plan_type=1） */
export function getPurchasePlanSummary(params: {
  date_begin?: string;
  date_end?: string;
  department_ids?: Array<number | string>;
  year?: number | string;
}) {
  return post<ReportPurchasePlanResponse>("/platform/report/year-purchase-summary", params);
}

/** 月度计划统计行（purchase_plan_type=2） */
export interface ReportMonthPurchasePlanRow {
  d: string;
  department_id: number | string;
  department_name?: string | null;
  goods_id?: number | string;
  spec_id?: number | string;
  goods_name?: string | null;
  spec_name?: string | null;
  code?: string | null;
  unit?: string | null;
  plan_quantity?: number | string;
  used_quantity?: number | string;
  return_quantity?: number | string;
  remain_quantity?: number | string;
  price?: number | string;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  /** 已使用比例（百分比） */
  usage_ratio?: number | string;
}

export interface ReportMonthPurchasePlanResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: ReportMonthPurchasePlanRow[];
  errors?: Record<string, string[]>;
}

/** 月度计划统计表（is_limit_quota + purchase_plan_type=2） */
export function getMonthPurchasePlanSummary(params: {
  date_begin?: string;
  date_end?: string;
  department_ids?: Array<number | string>;
  goods_ids?: Array<number | string>;
  spec_ids?: Array<number | string>;
  year?: number | string;
}) {
  return post<ReportMonthPurchasePlanResponse>("/platform/report/month-purchase-summary", params);
}

/** 报表导出类型（与查询接口一一对应） */
export type ReportExportType =
  | "month-summary"
  | "daily-summary"
  | "goods-summary"
  | "category-summary"
  | "details"
  | "year-purchase-summary"
  | "month-purchase-summary";

export interface ReportExportResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  /** 相对站点根路径，如 /download/xxx.xlsx */
  url?: string;
  errors?: Record<string, string[]>;
}

/** 报表导出 Excel（查询条件与对应报表接口相同，另传 report_type） */
export function exportReport(
  params: ReportFilterParams & {
    report_type: ReportExportType;
    year?: number | string;
  },
) {
  return post<ReportExportResponse>("/platform/report/export", params);
}
